import { BASE, apiFetch, safeFetch } from "./client";
import type {
  ForecastComparisonDoc,
  ForecastRunSummary,
  ForecastSnapshot,
} from "../forecast/types";

export function corpusExportUrl(matterId: string): string {
  return `${BASE}/matters/${encodeURIComponent(matterId)}/corpus/export`;
}

export interface UploadForecastResult {
  forecastRunId: string;
  snapshotCount: number;
  emailIds: number[];
  normalized: boolean;
}

export function uploadForecast(
  matterId: string,
  body: {
    name?: string;
    generatedBy?: string;
    snapshots: unknown[];
    workstreams: Array<{ id: string; name?: string; code?: string | null }>;
  },
): Promise<UploadForecastResult> {
  return apiFetch<UploadForecastResult>(`/matters/${encodeURIComponent(matterId)}/forecast`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function listForecasts(matterId: string): Promise<ForecastRunSummary[]> {
  const r = await safeFetch<{ forecasts: ForecastRunSummary[] }>(
    `/matters/${encodeURIComponent(matterId)}/forecasts`,
  );
  return (r && r.forecasts) || [];
}

export function getForecast(
  forecastRunId: string,
): Promise<{ snapshots: ForecastSnapshot[] } & ForecastRunSummary> {
  return apiFetch(`/forecast/${encodeURIComponent(forecastRunId)}`);
}

export function deleteForecast(forecastRunId: string): Promise<void> {
  return apiFetch(`/forecast/${encodeURIComponent(forecastRunId)}`, { method: "DELETE" });
}

export function compareForecast(
  forecastRunId: string,
  simulationRunId: string,
  actualSnapshots: ForecastSnapshot[],
): Promise<ForecastComparisonDoc | null> {
  return safeFetch<ForecastComparisonDoc>(`/forecast/${encodeURIComponent(forecastRunId)}/compare`, {
    method: "POST",
    body: JSON.stringify({ simulationRunId, actualSnapshots }),
  });
}

export function getComparison(
  forecastRunId: string,
  simulationRunId: string,
): Promise<ForecastComparisonDoc | null> {
  return safeFetch<ForecastComparisonDoc>(
    `/forecast/${encodeURIComponent(forecastRunId)}/compare/${encodeURIComponent(simulationRunId)}`,
  );
}

/* Clears the cached comparison for a (forecast, simulation) pair so resetting
   the simulation also clears the forecast panel. Idempotent server-side. */
export function deleteComparison(
  forecastRunId: string,
  simulationRunId: string,
): Promise<void> {
  return apiFetch(
    `/forecast/${encodeURIComponent(forecastRunId)}/compare/${encodeURIComponent(simulationRunId)}`,
    { method: "DELETE" },
  );
}
